import React, { useState } from "react";

import { CheckIcon, Square2StackIcon } from "@heroicons/react/24/outline";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";

import { useCopyToClipboard } from "../../hooks/useCopyToClipboard";
import { cn } from "../../utils/style";

type CodeBlocks = {
  code: string;
  language: string;
  displayLanguage: string;
}[];

type ElementType =
  | "a"
  | "blockquote"
  | "br"
  | "em"
  | "h1"
  | "h2"
  | "h3"
  | "h4"
  | "h5"
  | "h6"
  | "hr"
  | "img"
  | "li"
  | "ol"
  | "p"
  | "strong"
  | "ul";

type ChildrenCode = React.ReactElement<
  {
    children?: string;
    className?: string;
  },
  "code"
>;

type ChildrenPre = React.ReactElement<
  {
    children: ChildrenCode;
  },
  "pre"
>;

type ChildrenOther = React.ReactElement<unknown, ElementType>;

type Children = ChildrenPre | ChildrenPre[] | ChildrenOther;

function getHighlightLanguage(language: string) {
  switch (language) {
    case "curl":
    case "shell":
      return "shell";
    case "python":
      return "python";
    case "javascript":
    case "nodejs":
      return "javascript";
    case "langchain":
      return "python";
    case "llamaindex":
      return "python";
    default:
      return "plaintext";
  }
}

const displayLanguageMap = {
  curl: "cURL",
  python: "Python",
  javascript: "JavaScript",
  nodejs: "Node.js",
  shell: "Shell",
  langchain: "LangChain",
  llamaindex: "LlamaIndex",
  plaintext: "Plain Text",
};

function getCodeAndLanguage({ children }: { children?: Children }): CodeBlocks {
  if (!children) return [];

  const isSingleLanguage = !Array.isArray(children);
  const elements = isSingleLanguage ? [children] : children;

  const codeBlocks = elements.reduce<CodeBlocks>((prev: CodeBlocks, curr) => {
    const isPreElement = curr.type === "pre";
    if (!isPreElement) return [...prev];

    // prettier-ignore
    const code = curr.props.children.props.children?.split("\n").slice(0, -1).join("\n");
    const languageClass = curr.props.children.props.className;
    const language = languageClass
      ? languageClass.replace("language-", "").toLowerCase()
      : "plaintext";

    const isValidFormat = (code && language) || code;
    if (!isValidFormat) return [...prev];

    // code and language are both valid
    return [
      ...prev,
      {
        code,
        language: getHighlightLanguage(language),
        displayLanguage:
          displayLanguageMap[language as keyof typeof displayLanguageMap] ||
          language,
      },
    ];
  }, []);

  return codeBlocks;
}

export function CodeBlock({ children }: { children: Children }) {
  const [selectedCodeIdx, setSelectedCodeIdx] = useState(0);
  const { isCopied, copyToClipboard } = useCopyToClipboard(1000);

  const codeBlocks = getCodeAndLanguage({ children });
  const hasCodeBlocks = codeBlocks.length > 0;

  const handleClickCopy = () => {
    copyToClipboard({
      text: codeBlocks[selectedCodeIdx].code,
      isRepeatable: true,
    });
  };

  const handleClickChangeLanguage = (idx: number) => {
    setSelectedCodeIdx(idx);
  };

  if (!hasCodeBlocks) return null;

  return (
    <div className="inline-code flex flex-col">
      <div className="flex flex-row items-end gap-4 overflow-x-auto rounded-t-lg bg-muted px-4 text-muted-dark scrollbar-hide">
        {codeBlocks.map(({ language, displayLanguage }, idx) => (
          <button
            data-state={idx === selectedCodeIdx ? "active" : "inactive"}
            key={language}
            className={cn(
              `whitespace-nowrap border-b border-transparent py-2 transition-colors disabled:pointer-events-none disabled:opacity-50`,
              `subtle-semibold mobile:subtle`,
              `hover:text-surface-foreground data-[state=active]:border-primary data-[state=active]:text-surface-foreground`,
            )}
            onClick={() => handleClickChangeLanguage(idx)}
          >
            {displayLanguage}
          </button>
        ))}
      </div>
      <figure className="relative overflow-hidden rounded-b-lg bg-subtle">
        <button
          type="button"
          className="absolute right-2 top-2 z-[2] inline-flex size-[30px] items-center justify-center rounded-md p-2 text-sm font-medium backdrop-blur-sm transition-all duration-100 hover:bg-muted hover:text-accent-foreground disabled:pointer-events-none disabled:opacity-50 group-hover:opacity-100"
          aria-label="Copy Text"
          onClick={handleClickCopy}
        >
          <CheckIcon
            className={cn(
              "absolute size-3.5 transition-transform",
              !isCopied ? "scale-0" : "",
            )}
          />
          <Square2StackIcon
            className={cn(
              "absolute size-3.5 transition-transform",
              isCopied ? "scale-0" : "",
            )}
          />
        </button>
        <div className="relative overflow-hidden mobile:h-[calc(100dvh*0.35)]">
          <div className="size-full overflow-scroll">
            <SyntaxHighlighter
              language={codeBlocks[selectedCodeIdx].language}
              customStyle={{
                margin: 0,
                overflowWrap: "break-word",
                wordWrap: "break-word",
                padding: "1rem",
                borderRadius: "0 0 16px 16px",
                overflow: "visible",
                fontSize: "12px",
                maxHeight: "400px",
                background: "transparent",
              }}
            >
              {codeBlocks[selectedCodeIdx].code}
            </SyntaxHighlighter>
          </div>
        </div>
      </figure>
    </div>
  );
}
